import type { ContentHash, ContentRef } from "@cogo/publisher-core";

import {
  assertApplicationEvidenceRole,
  assertApplicationOntologyGenerationBindingRoleIntegrity,
  assertPlainJsonContract,
  type ApplicationEvidenceRole,
  type ApplicationEvidenceRoleResolver,
  type ApplicationOntologyGenerationBinding,
} from "./application-ontology-generation-binding";

export type { ApplicationEvidenceRole, ApplicationEvidenceRoleResolver };

export interface GeneratedApplicationReadbackObservation {
  schemaVersion: "cogo.generated-application-readback-observation.v1";
  binding: ApplicationOntologyGenerationBinding;
  generationEvidenceEnvelopeRef: ContentRef;
  applicationPackageRef: ContentRef;
  artifactRef: ContentRef;
  artifactValidationObservationRef: ContentRef;
  artifactPersistenceEligibilityRef: ContentRef;
  sourceTreeRef: ContentRef;
  runtimeRef: ContentRef;
  persistenceWriteRef: ContentRef;
  freshReadbackRef: ContentRef;
  readbackArtifactRef: ContentRef;
  contentHash: ContentHash;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const KEYS = [
  "schemaVersion", "binding", "generationEvidenceEnvelopeRef",
  "applicationPackageRef", "artifactRef", "artifactValidationObservationRef",
  "artifactPersistenceEligibilityRef", "sourceTreeRef", "runtimeRef",
  "persistenceWriteRef", "freshReadbackRef", "readbackArtifactRef", "contentHash",
] as const;

function plain(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype;
}
function exact(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return plain(value) && Object.keys(value).length === keys.length &&
    Object.keys(value).every((key) => keys.includes(key)) &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}
function reference(value: unknown): value is ContentRef {
  return exact(value, ["ref", "contentHash"]) && typeof value.ref === "string" &&
    value.ref.trim().length > 0 && typeof value.contentHash === "string" &&
    HASH.test(value.contentHash);
}
export function assertGeneratedApplicationReadbackObservationIntegrity(
  value: unknown,
  resolver: ApplicationEvidenceRoleResolver,
): GeneratedApplicationReadbackObservation {
  assertPlainJsonContract(value, "generated-application-readback-observation");
  if (!exact(value, KEYS) ||
    value.schemaVersion !== "cogo.generated-application-readback-observation.v1" ||
    typeof value.contentHash !== "string" || !HASH.test(value.contentHash)) {
    throw new Error("generated-application-readback-observation-invalid");
  }
  assertApplicationOntologyGenerationBindingRoleIntegrity(value.binding, resolver);
  const expected = {
    generationEvidenceEnvelopeRef: "generation-evidence-envelope",
    applicationPackageRef: "application-package",
    artifactRef: "generated-artifact",
    artifactValidationObservationRef: "artifact-validation-observation",
    artifactPersistenceEligibilityRef: "artifact-persistence-eligibility",
    sourceTreeRef: "source-tree",
    runtimeRef: "runtime",
    persistenceWriteRef: "persistence-write",
    freshReadbackRef: "fresh-readback",
    readbackArtifactRef: "readback-artifact",
  } as const satisfies Record<string, ApplicationEvidenceRole>;
  for (const [field, expectedRole] of Object.entries(expected) as [
    keyof typeof expected,
    ApplicationEvidenceRole,
  ][]) {
    const referenceValue = value[field];
    if (!reference(referenceValue)) {
      throw new Error("generated-application-readback-observation-invalid");
    }
    assertApplicationEvidenceRole(resolver, field, referenceValue, expectedRole);
  }
  return value as unknown as GeneratedApplicationReadbackObservation;
}
